/**
 * Reine Logik der Datenschutzseite (#/datenschutz/unterweisung?…): Text der
 * Firma oder Muster waehlen, Platzhalter fuellen und den Klartext in Bloecke
 * zerlegen. Die Seite rendert nur diese Bloecke — React-Elemente statt HTML,
 * damit ein gepflegter Text nie Markup in die Seite schleusen kann.
 *
 * Format (wie DatenschutzText.text): Leerzeile trennt Absaetze, `## ` macht
 * eine Ueberschrift, `- ` einen Listenpunkt; einzelne Umbrueche bleiben im
 * Absatz (Anschriften). Offene `[…]`-Stellen werden markiert.
 */
import type { DatenschutzText } from '../../../services/sifa/types';
import { DATENSCHUTZ_MUSTER_TEXT, DATENSCHUTZ_MUSTER_TITEL, fuelleDatenschutz } from '../../../services/sifa/datenschutzMuster';

/** Das eingebaute Muster als DatenschutzText — id null, Fassung 0. */
export const MUSTER_DATENSCHUTZ: DatenschutzText = {
  id: null,
  fassung: 0,
  titel: DATENSCHUTZ_MUSTER_TITEL,
  text: DATENSCHUTZ_MUSTER_TEXT,
};

/** Fassung der Firma, sonst das Muster — ein leerer Text gilt als keine Fassung. */
export const datenschutzOderMuster = (d: DatenschutzText | null | undefined): DatenschutzText =>
  d && (d.text ?? '').trim() ? d : MUSTER_DATENSCHUTZ;

/** Das Muster erkennt man an der fehlenden id; die Fassungsnummer allein waere zu weich. */
export const istMuster = (d: DatenschutzText): boolean => !d.id;

/** „Stand: Fassung 3" bzw. „Muster". */
export const standText = (d: DatenschutzText): string => (istMuster(d) ? 'Muster' : `Stand: Fassung ${d.fassung}`);

/** Ein Stueck Text; `luecke` = noch offene `[…]`-Stelle, die sichtbar markiert wird. */
export interface TextStueck {
  text: string;
  luecke: boolean;
}

export type DatenschutzBlock =
  | { art: 'ueberschrift'; stuecke: TextStueck[] }
  | { art: 'absatz'; zeilen: TextStueck[][] }
  | { art: 'liste'; punkte: TextStueck[][] };

/** Gleiches Muster wie offenePlatzhalter (datenschutzMuster.ts): `[` … `]` innerhalb einer Zeile. */
const LUECKE = /\[[^[\]\n]+\]/g;

/** Zerlegt eine Zeile in Text und offene `[…]`-Stellen; leere Stuecke fallen weg. */
export const markiereLuecken = (zeile: string): TextStueck[] => {
  const s = zeile ?? '';
  const out: TextStueck[] = [];
  let pos = 0;
  for (const m of s.matchAll(LUECKE)) {
    const start = m.index ?? 0;
    if (start > pos) out.push({ text: s.slice(pos, start), luecke: false });
    out.push({ text: m[0], luecke: true });
    pos = start + m[0].length;
  }
  if (pos < s.length) out.push({ text: s.slice(pos), luecke: false });
  return out;
};

// Auch ein nacktes „##" ist eine (leere) Ueberschrift — sie faellt weg, statt als Text stehen zu bleiben.
const UEBERSCHRIFT = /^\s*#{1,6}(?:\s+(.*))?$/;
const LISTENPUNKT = /^\s*[-*•]\s+(.*)$/;

/**
 * Klartext → Bloecke. Eine Ueberschrift oder ein Listenpunkt beendet einen
 * laufenden Absatz auch ohne Leerzeile; aufeinanderfolgende Listenpunkte
 * bilden eine Liste.
 */
export const datenschutzBloecke = (text: string | null | undefined): DatenschutzBlock[] => {
  const bloecke: DatenschutzBlock[] = [];
  let absatz: TextStueck[][] | null = null;
  let liste: TextStueck[][] | null = null;
  const abschliessen = () => {
    if (absatz?.length) bloecke.push({ art: 'absatz', zeilen: absatz });
    if (liste?.length) bloecke.push({ art: 'liste', punkte: liste });
    absatz = null;
    liste = null;
  };
  for (const roh of (text ?? '').replace(/\r\n?/g, '\n').split('\n')) {
    const zeile = roh.trimEnd();
    if (!zeile.trim()) {
      abschliessen();
      continue;
    }
    const u = UEBERSCHRIFT.exec(zeile);
    if (u) {
      abschliessen();
      const titel = (u[1] ?? '').trim();
      if (titel) bloecke.push({ art: 'ueberschrift', stuecke: markiereLuecken(titel) });
      continue;
    }
    const l = LISTENPUNKT.exec(zeile);
    if (l) {
      if (!liste) abschliessen();
      liste = liste ?? [];
      liste.push(markiereLuecken(l[1].trim()));
      continue;
    }
    if (!absatz) abschliessen();
    absatz = absatz ?? [];
    absatz.push(markiereLuecken(zeile.trim()));
  }
  abschliessen();
  return bloecke;
};

/** Was die Seite anzeigt: Titel, Stand, gefuellter Text und ob noch Luecken offen sind. */
export interface DatenschutzAnzeige {
  titel: string;
  stand: string;
  muster: boolean;
  bloecke: DatenschutzBlock[];
  offeneLuecken: boolean;
}

/**
 * Fuellt `{{ARBEITGEBER}}` mit dem Kunden und `{{AUFTRAGSVERARBEITER}}` mit
 * dem Buero. Fehlt ein Name, bleibt eine markierte Luecke (fuelleDatenschutz).
 */
export const datenschutzAnzeige = (
  d: DatenschutzText | null | undefined,
  namen: { kundeName?: string | null; bueroName?: string | null },
): DatenschutzAnzeige => {
  const fassung = datenschutzOderMuster(d);
  const text = fuelleDatenschutz(fassung.text, {
    arbeitgeber: namen.kundeName ?? '',
    auftragsverarbeiter: namen.bueroName ?? '',
  });
  const bloecke = datenschutzBloecke(text);
  const offeneLuecken = bloecke.some((b) =>
    (b.art === 'ueberschrift' ? [b.stuecke] : b.art === 'absatz' ? b.zeilen : b.punkte).some((z) => z.some((s) => s.luecke)),
  );
  return {
    titel: (fassung.titel ?? '').trim() || DATENSCHUTZ_MUSTER_TITEL,
    stand: standText(fassung),
    muster: istMuster(fassung),
    bloecke,
    offeneLuecken,
  };
};
